import { alpha, Theme } from '@mui/material';
import { Fade } from '@mui/material';
import type { MenuProps, SxProps } from '@mui/material';
import { useState } from 'react';

// Dropdown menus in the top bar (Data, account) look like part of the bar: they open flush against its bottom
// edge with square top corners, the bar's blurred surface and border, nav-button typography, and the brand green for
// hover and the current page.

export const navMenuPaperSx: SxProps<Theme> = (theme) => ({
  minWidth: 220,
  borderRadius: '0 0 12px 12px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
  borderTop: 'none',
  // Matches how the translucent app bar renders over the page, so bar and menu read as one surface.
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.97)' : 'rgba(252, 252, 252, 0.97)',
  backgroundImage: 'none',
  backdropFilter: 'blur(12px)',
  boxShadow: theme.palette.mode === 'dark' ? '0 16px 24px -12px rgba(0, 0, 0, 0.6)' : '0 16px 24px -12px rgba(0, 0, 0, 0.15)',
  '& .MuiList-root': { p: 0.75 },
});

export const navMenuItemSx: SxProps<Theme> = (theme) => ({
  borderRadius: 1.5,
  px: 1.5,
  py: 1,
  my: '2px',
  fontSize: '0.875rem',
  fontWeight: 500,
  letterSpacing: '0.02857em',
  textTransform: 'uppercase',
  position: 'relative',
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
  },
  '&.Mui-selected::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 32 },
});

/**
 * Open/close state for a top-bar menu, positioned at the bar's bottom edge (not the button's), aligned with the
 * button's left or right edge. Spread `menuProps` onto <Menu>.
 */
export const useNavMenu = (align: 'left' | 'right') => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const open = (e: React.MouseEvent<HTMLElement>) => {
    const button = e.currentTarget.getBoundingClientRect();
    const bar = (e.currentTarget.closest('header') ?? e.currentTarget).getBoundingClientRect();
    // 1px up so the menu covers the bar's bottom border where they meet, and reads as one surface.
    setPos({ top: bar.bottom - 1, left: align === 'left' ? button.left : button.right });
  };
  const close = () => setPos(null);
  const menuProps: Partial<MenuProps> & { open: boolean } = {
    open: pos !== null,
    onClose: close,
    anchorReference: 'anchorPosition',
    anchorPosition: pos ?? undefined,
    transformOrigin: { vertical: 'top', horizontal: align },
    marginThreshold: 0,
    // A quick fade: Material's default grow-from-the-button animation makes the menu look detached from the bar.
    TransitionComponent: Fade,
    transitionDuration: 120,
    slotProps: { paper: { sx: navMenuPaperSx } },
  };
  return { isOpen: pos !== null, open, close, menuProps };
};
